import { useSetAtom } from "jotai";
import { useRouter } from "next/router";
import { useCallback } from "react";

import authApi from "@/apis/auth/authApi";
import commonApi from "@/apis/common/commonApi";
import { authUserAtom } from "@/states/auth/atoms";
import { Customer } from "@/states/auth/types";

export const useLogout = () => {
  const router = useRouter();
  const setAuthUser = useSetAtom(authUserAtom);

  return useCallback(
    async (redirectTo: string | undefined = undefined) => {
      setAuthUser((prev) => ({ ...prev, isLoading: true }));
      await authApi.logout();
      await router.push(redirectTo ? redirectTo : "/logout");
      setAuthUser(() => ({
        error: undefined,
        user: undefined,
        isLoading: false,
        isInitialized: true,
      }));
    },
    [router, setAuthUser],
  );
};

export interface LoginInput {
  email: string;
  password: string;
  remember?: boolean;
}

export const useLogin = () => {
  const setAuthUser = useSetAtom(authUserAtom);

  return useCallback(
    async ({ email, password, remember }: LoginInput) => {
      setAuthUser((prev) => ({
        ...prev,
        isLoading: true,
        isInitialized: true,
      }));

      try {
        await authApi.login({
          postLoginRequest: {
            email,
            password,
            remember,
          },
        });

        await commonApi.getCsrfCookie();

        const user: Customer = await authApi.get();
        setAuthUser((prev) => ({
          ...prev,
          user: user,
          isLoading: false,
          error: undefined,
        }));
      } catch (e) {
        setAuthUser(() => ({
          user: undefined,
          isLoading: false,
          isInitialized: true,
          error: e as Error,
        }));
      }
    },
    [setAuthUser],
  );
};

export const useRefreshAuthUser = () => {
  const setAuthUser = useSetAtom(authUserAtom);

  return useCallback(async () => {
    setAuthUser((prev) => ({
      ...prev,
      isLoading: true,
      isInitialized: true,
    }));

    try {
      const user = await authApi.get();
      setAuthUser((prev) => ({
        ...prev,
        isLoading: false,
        user: user,
      }));
    } catch (e) {
      setAuthUser(() => ({
        error: e as Error,
        user: undefined,
        isLoading: false,
        isInitialized: true,
      }));
    }
  }, [setAuthUser]);
};

export const useInitialize = () => {
  const setAuthUser = useSetAtom(authUserAtom);

  return useCallback(async () => {
    setAuthUser((prev) => ({
      ...prev,
      isLoading: true,
      isInitialized: true,
    }));

    try {
      await commonApi.getCsrfCookie();
      // const user = await authApi.get();
      // setAuthUser((prev) => ({
      //   ...prev,
      //   isLoading: false,
      //   user: user,
      // }));
    } catch (e) {
      setAuthUser((prev) => ({
        ...prev,
        error: undefined,
        user: undefined,
        isLoading: false,
      }));
    }
  }, [setAuthUser]);
};

export const useSetUnauthenticated = () => {
  const setAuthUser = useSetAtom(authUserAtom);

  return useCallback(() => {
    setAuthUser((prev) => ({
      ...prev,
      user: undefined,
    }));
  }, [setAuthUser]);
};
